/**
 * Documentation
 * Use a button element as the actuator.
 * Once the popup is shown, the button becomes an <input type="text"> element so the user may input characters in the
 *  field and search the popup's <li> textContent for a match.
 *  The match is performed character by character from the start of the string value.
 *
 * Usage:
 * <button class="block border-0 bg-transparent text-white
 *                hover:no-underline hover:shadow-none
 *                focus:no-underline focus:shadow-none focus:outline-none focus:ring-0"
 *    type="button"
 *    data-br-dropdown                          -- Initialization: required
 *    data-br-dropdown-v-placement="bottom"     -- vertical location of popup relative to the calling element: optional, defaults to 'top'
 *    data-br-dropdown-h-placement="right"      -- horizontal location of popup relative to the calling element: optional, defaults to 'center'
 *    data-br-target="#thePopup"                -- the querySelector() of the popup to pop.
 *    aria-controls="thePopup"
 *    aria-expanded="false"
 *    aria-label="Toggle dropdown">
 * </button>
 *  ...
 *  ...
 *  The container or popup element must be structured as noted below. <div><ul><li></li>...</ul></div>
 * <div id="thePopup" class="hidden opacity-0 absolute mt-1 z-20">
 *     <ul>
 *         <li></li>
 *         ...
 *     </ul>
 * </div>
 */

import { addListener, removeListener } from "./support/utils";

class BRDropdown
{
    static dropdownInstances = new Map();

    constructor(ddElement, options={})
    {
        if (ddElement && BRDropdown.getInstance(ddElement) === undefined ) {

            this.brDdButton = ddElement;
            const htmlOptions = {};
            const data = this.brDdButton.dataset;

            if (this.brDdButton.id === '') {
                const uuid = this.generateUUID();
                this.brDdButton.setAttribute('id', uuid)
            }

            Object.keys(data).forEach(key => {
                if ( key.startsWith('brDropdown') ) {
                    const optionName = key.replace('brDropdown', '').toLowerCase();
                    htmlOptions[optionName] = data[key] === 'false' ? false : (data[key] === 'true' ? true : data[key]);
                }
            });

            // Default options
            const defaultOptions = {
                trigger: 'click',    // Available options: none, hover, click
                vplacement: "top",
                hplacement: "center",
                showall: true,       // When the dropdown is shown, remove 'hidden' class from all the elements within.
                searchable: false,   // Convert the button to an input so the drop-down list can be searched
                static: false        // false = clicks outside the dropdown view close the view.
            };

            const normalizedDefaultOptions = {};
            Object.keys(defaultOptions).forEach(key => {
                normalizedDefaultOptions[key.toLowerCase()] = defaultOptions[key];
            });

            this.options = {
                ...normalizedDefaultOptions,
                ...htmlOptions,
                ...options
            }

            this.brView = document.querySelector( this.brDdButton.getAttribute('data-br-target') );

            this.setBody = this.setBody.bind(this);
            this.toggle = this.toggle.bind(this);
            this.show = this.show.bind(this);
            this.closeView = this.closeView.bind(this);
            this.setSelectedValue = this.setSelectedValue.bind(this);
            this.searchDropdownValues = this.searchDropdownValues.bind(this);

            if (this.options.trigger !== 'none') {
                if (this.options.trigger === 'hover') {
                    addListener('mouseenter', this.brDdButton, this.toggle);
                    addListener('mouseleave', this.brDdButton, this.toggle);
                }
                else {
                    addListener(this.options.trigger, this.brDdButton, this.toggle);
                }
            }
            if (!this.options.static) {
                if (this.options.trigger === 'hover') {
                    addListener('mouseleave', document, this.closeView);
                }
                else {
                    addListener('click', document, this.closeView);
                }
            }

            BRDropdown.dropdownInstances.set(ddElement, this);
        }
    }

    generateUUID()
    {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
            const r = Math.random() * 16 | 0;
            const v = c === 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });
    }

    setSelectedValue(evt)
    {
        // Get the text && Set the button
        const callerItem = evt.currentTarget;
        let btnTextElement = undefined;
        if ( this.brDdButton.nodeName === 'BUTTON' ) {
            btnTextElement = this.brDdButton.children[0];
            btnTextElement.classList.remove('text-gray-500');
            btnTextElement.innerHTML = callerItem.textContent;
        }
        else {
            btnTextElement = this.brDdButton;
            btnTextElement.value = callerItem.textContent;
        }

        if ( callerItem.hasAttribute('data-id') ) {
            this.brDdButton.setAttribute('data-value', callerItem.getAttribute('data-id'));
        }

        // Emit the 'br-dd-changed' event
        const event = new CustomEvent('br-dd-changed', {
            detail: {
                target: this.brDdButton,
                value : this.brDdButton.getAttribute('data-value'),
                text  : callerItem.textContent
            }
        });

        // Close the DD
        this.hide();

        // Dispatch the event to the button
        this.brDdButton.dispatchEvent(event);
    }

    setBody(value)
    {
        this.brView.innerHTML = value;
    }

    toggle(evt)
    {
        let hasClass = this.brView.classList.contains('hidden');
        if (hasClass) {
            this.show(evt);
        }
        else {
            this.hide(evt);
        }
    }

    show(evt)
    {
        const buttonElement = evt.currentTarget; // this is the this.brDdButton, I think.

        if (this.options.searchable === true) {
            // Convert the button to an input so we can search this.brView for a match
            const buttonClasses = buttonElement.classList;
            this.newInput = document.createElement('input');
            this.newInput.type = 'text';
            this.newInput.placeholder = buttonElement.textContent.trim();
            this.newInput.style.fontSize = '95%';
            buttonClasses.forEach(className => {
                this.newInput.classList.add(className);
            });
            // Add a listener to search the dropdown for the text input in the input
            addListener('input', this.newInput, this.searchDropdownValues);
        }

        // When the data-selector= is set to 'default' the dropdown acts as a <select> element.
        this.showHandler = this.setSelectedValue.bind(this);
        addListener('click', this.brView.querySelectorAll(':scope > ul > li[data-selector="default"]'), this.showHandler);

        // Reset any hidden dropdown values
        if (this.options.showall === true) {
            this.brView.querySelectorAll(':scope > ul > li[data-selector="default"]').forEach(el => {
                el.classList.remove('hidden');
            });
        }

        // Position the popOver
        this.brView.classList.remove('hidden');
        this.brView.setAttribute('data-opened-by', this.brDdButton.id);
        this.#setDropdownLocation(buttonElement);

        if (this.options.searchable === true) {
            // Replace the button with the input
            buttonElement.replaceWith(this.newInput);
            this.newInput.focus();
        }

        // Emit the 'br-dd-opened' event
        const event = new CustomEvent('br-dd-opened', {
            detail: {
                target: this.options.searchable === true ? this.newInput : this.brDdButton,
                value : this.brView,
            }
        });
        if ( this.options.searchable === true ) {
            // Dispatch the event to the input
            this.newInput.dispatchEvent(event);
        }
        else {
            // Dispatch the event to the button
            this.brDdButton.dispatchEvent(event);
        }

        this.brView.classList.remove('opacity-0');
        this.brDdButton.setAttribute('aria-expanded', "true")
    }

    hide()
    {
        // When the data-selector= is set to 'default' the dropdown acts as a <select> element.
        removeListener('click', this.brView.querySelectorAll(':scope > ul > li[data-selector="default"]'), this.setSelectedValue);

        if (this.options.searchable === true) {
            this.newInput.replaceWith(this.brDdButton);
        }

        this.brView.classList.add('hidden');
        this.brView.classList.add('opacity-0');
        this.brDdButton.setAttribute('aria-expanded', "false");
        this.brView.removeAttribute('data-opened-by');

        // Remove the background
        if ( this.brView.nextElementSibling !== null && this.brView.nextElementSibling.getAttribute('data-br-backdrop') !== null ) {
            this.brView.nextElementSibling.remove();
        }

        // Remove the event listeners
        removeListener('click', this.brView.querySelectorAll(':scope > ul > li[data-selector="default"]'), this.showHandler);
    }

    searchDropdownValues(e)
    {
        const input = e.currentTarget;
        if (input.value !== null && input.value !== undefined && input.value !== '') {
            // Show/hide the <li> elements
            this.brView.querySelectorAll(':scope > ul > li[data-selector="default"]').forEach(el => {
                if ( ! el.textContent.toLowerCase().startsWith(input.value.toLowerCase()) ) {
                    el.classList.add('hidden');
                }
                else {
                    el.classList.remove('hidden');
                }
            });
        }
    }

    #setDropdownLocation(callerElement)
    {
        // Calculate target's position relative to the document
        const callerRect = callerElement.getBoundingClientRect();

        // Find the relatively positioned parent
        const positionedParent = this.#findPositionedParent(this.brView.parentElement);
        const parentRect = positionedParent.getBoundingClientRect();
        const openElement = this.brView.getBoundingClientRect();


        const scrollTop = 0;//window.scrollY;
        const scrollLeft = 0;//window.scrollX;

        // Vertical placement
        if (this.options.vplacement === 'top') {
            this.brView.style.top = `${ scrollTop - parentRect.top + callerRect.top - this.brView.offsetHeight }px`;
        }
        else if (this.options.vplacement === 'bottom') {
            this.brView.style.top = `${ scrollTop - parentRect.top + callerRect.top + callerRect.height + 1 }px`;
        }
        else {
            // vplacement is a class name. Attach the class
            if (this.options.vplacement !== '')
                this.brView.classList.add(this.options.vplacement);
        }

        // Horizontal placement
        if (this.options.hplacement === 'right') {
            this.brView.style.left = `${ scrollLeft - parentRect.left + callerRect.left + callerRect.width - this.brView.offsetWidth }px`;
        }
        else if (this.options.hplacement === 'left') {
            this.brView.style.left = `${ scrollLeft + callerRect.left - parentRect.left }px`;
        }
        else if (this.options.hplacement === 'center') {
            let remainder = callerRect.width - openElement.width;
            this.brView.style.left = `${ (scrollLeft + callerRect.left - parentRect.left) + (remainder/2) }px`;
        }
        else if (this.options.hplacement === 'full') {
            if (openElement.width < callerRect.width) {
                // Expand width
                this.brView.style.width = `${callerRect.width}px`;
            }
            // Set left
            this.brView.style.left = `${ scrollLeft + callerRect.left - parentRect.left }px`;
        }
        else {
            // hplacement is a class name. Attach the class.
            if (this.options.hplacement !== '')
                this.brView.classList.add(this.options.hplacement);
        }
    }

    #findPositionedParent(element)
    {
        while (element) {
            const style = getComputedStyle(element);
            if ( style.position !== 'static' ) {
                return element;
            }
            element = element.parentElement;
        }
        return document.body;
    }

    closeView(event)
    {
        // If the click event was made outside the brView, then close the view.

        // ((
        // If the event.target !== button that opened the dropdown
        //  AND
        // the event.target is outside the dropdown container
        //  AND
        // // the dropdown is visible
        // ))
        //  OR
        // ((                                                       }
        // the event.target === button that opened the dropdown     }
        //  AND                                                     } This should be handled in the toggle above.
        // the dropdown is visible                                  }
        // ))                                                       }

        if (
            !this.brDdButton.contains(event.target) &&
            !this.brView.contains(event.target) &&
            !['hidden', '!hidden'].some(className => this.brView.classList.contains(className)) &&
            this.brView.getAttribute('data-opened-by') === this.brDdButton.id
        ) {
            this.hide();
        }
    }

    static initAll(parent)
    {
        if (!parent) parent = document;

        parent.querySelectorAll('[data-br-dropdown]').forEach(ddElement => {
            if (!BRDropdown.dropdownInstances.has(ddElement)) {
                new BRDropdown(ddElement);
            }
        });
    }

    // Static method to access a widget instance from a DOM element
    static getInstance(ddElement) {
        return BRDropdown.dropdownInstances.get(ddElement);
    }
}

export default BRDropdown;